// The audio-call experience (Layer B3/B5) — deliberately NOT the meeting UI.
//
// A call is one conversation, so the screen is a single calm canvas: the other
// person, large, with a speaking halo; their name; the call status (Ringing… →
// 02:14). A group call keeps that screen and simply shows everyone who has
// answered (CallStage). No video grid, no panels, no meeting chrome.
//
// Controls are CALL-only: mic · speaker · add person · hang up. Everything a
// meeting offers (share, reactions, hand, layout, chat/files/tools, record) is
// intentionally absent.
//
// §16.3 is NOT negotiable even here: the E2EE badge and the recording indicator
// stay unmissable — they survive any design.

import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { api, meetingShareLink } from '../api';
import { startRingback } from '../sounds';
import { engine } from '../engine';
import { probeRender } from '../perf/renderProbe';
import { pushToast, useCallStore, useCallStoreShallow } from '../store/callStore';
import { IconLeave, IconMicOff, IconMicrophone, IconPersonAdd, IconSpeaker } from '../ui/icons';
import { CallStage, RingingPeer, useRemoteIdentities, usePeerTitle } from './CallStage';
import { CallTarget, NewCallDrawer } from './NewCallDrawer';
import { DeviceMenu } from './DeviceMenu';
import { EncryptionBadge } from './EncryptionBadge';
import { KnockToast } from './KnockToast';
import { RecordingIndicator } from './RecordingIndicator';
import { RemoteAudio } from './RemoteAudio';

// The speaker control picks the audio OUTPUT device.
const OUTPUT_KINDS: MediaDeviceKind[] = ['audiooutput'];

/** Call duration, isolated so its 1s tick never re-renders the call screen. */
const CallTimer = memo(function CallTimer() {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSec((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const m = String(Math.floor(sec / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return (
    <span className="bz-acall-timer">
      {m}:{s}
    </span>
  );
});

export function AudioCallView({
  callId,
  slug,
  peerName,
  peerAvatar,
  invited,
  isCaller,
  endedReason,
  onHangUp,
}: {
  /** The call this screen is showing — needed to add people to it. */
  callId: string;
  slug: string;
  peerName: string;
  /** Their photo, for the 1:1 screen before they pick up. */
  peerAvatar?: string;
  /** Everyone rung who has not answered yet — they get a dim tile. */
  invited: RingingPeer[];
  /** Caller hears ringback until the other side joins; the callee never does. */
  isCaller: boolean;
  /** Remote resolution to display ("Call declined", "No answer", …). */
  endedReason: string | null;
  /** The page owns the leave lifecycle (cancel vs end, replace-navigation). */
  onHangUp: () => void;
}) {
  probeRender('AudioCallView');
  const isHost = useCallStore((s) => s.isHost);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Who we are still waiting on. Seeded from the call, extended when WE add
  // someone; anyone who answers drops out of it by turning up in the room.
  const [rung, setRung] = useState<RingingPeer[]>(invited);

  // Everyone else in the room. Identities only — the tiles subscribe to their
  // own slice, so a group call repaints one tile when someone speaks (§9.3.1).
  const identities = useRemoteIdentities();
  // Ringback for the caller until the FIRST person joins. Dep on the BOOLEAN so
  // speaking flips (or a fourth person arriving) never restart the tone.
  const hasPeer = identities.length > 0;
  useEffect(() => {
    if (!isCaller || hasPeer || endedReason) return;
    const stop = startRingback();
    return stop;
  }, [isCaller, hasPeer, endedReason]);

  const micEnabled = useCallStore((s) =>
    s.localIdentity ? (s.attendees[s.localIdentity]?.micEnabled ?? false) : false,
  );

  const toggleMic = useCallback(() => {
    void engine.setMicEnabled(!engine.snapshots().find((a) => a.isLocal)?.micEnabled);
  }, []);
  const toggleDevices = useCallback(() => setDevicesOpen((v) => !v), []);
  const closeDevices = useCallback(() => setDevicesOpen(false), []);

  // "Add someone": pick from the directory and RING them into this call — the
  // same drawer the Calls page starts a call with. Copying the room link stays
  // available inside it, because a link reaches people the directory can't.
  const openAdd = useCallback(() => setAddOpen(true), []);
  const closeAdd = useCallback(() => setAddOpen(false), []);

  const copyLink = useCallback(() => {
    void navigator.clipboard
      .writeText(meetingShareLink(`/m/${slug}`, null))
      .then(() => pushToast('Call link copied — they can ask to join'))
      .catch(() => pushToast('Could not copy the link', 'warning'));
  }, [slug]);

  const invite = useCallback(
    async (targets: CallTarget[]) => {
      try {
        await api.inviteToCall(callId, targets);
        setRung((cur) => [...cur, ...targets.filter((t) => !cur.some((p) => p.id === t.id))]);
        setAddOpen(false);
        pushToast(
          targets.length === 1
            ? `Ringing ${targets[0]!.name}…`
            : `Ringing ${targets.length} more people…`,
        );
      } catch (e) {
        pushToast(e instanceof Error ? e.message : 'Could not add them', 'error');
      }
    },
    [callId],
  );

  // Everyone already here, so the drawer can't offer them again. Attendee
  // identities are `u_<userId>` (see lkadapter.AttendeeIdentity).
  const here = useCallStoreShallow((s) => s.order.map((i) => i.replace(/^u_/, '')));


  const title = usePeerTitle(identities, peerName || 'Connecting…');
  // A tile already names everyone in a group, so the screen must not print the
  // same identities again as a headline.
  const isGroup = identities.length + rung.length > 1;
  const ringing = useMemo(
    () => rung.filter((p) => !identities.includes(`u_${p.id}`)),
    [rung, identities],
  );

  return (
    <div className="bz-acall">
      {/* §16.3: both trust signals stay unmissable in the call experience too. */}
      <div className="bz-acall-top">
        <EncryptionBadge />
        <RecordingIndicator />
      </div>

      <div className="bz-acall-stage">
        <CallStage
          identities={identities}
          ringing={ringing}
          fallbackName={peerName}
          fallbackAvatar={peerAvatar}
        />
        {/* One person: their name IS the headline. A group: the tiles carry the
            names, so all that is left to say is how many and how long. */}
        {!isGroup && <h1 className="bz-acall-name">{title}</h1>}
        {endedReason ? (
          <span className="bz-acall-status">{endedReason}</span>
        ) : hasPeer ? (
          <span className={isGroup ? 'bz-acall-sub bz-acall-sub-group' : 'bz-acall-sub'}>
            {isGroup && <span>{identities.length + 1} on the call</span>}
            <CallTimer />
          </span>
        ) : (
          <span className="bz-acall-status">Ringing…</span>
        )}
      </div>

      <div className="bz-acall-controls">
        <div className="bz-acall-pill">
          <button
            type="button"
            className={micEnabled ? 'bz-acall-btn' : 'bz-acall-btn bz-acall-btn-off'}
            onClick={toggleMic}
            aria-label={micEnabled ? 'Mute' : 'Unmute'}
            title={micEnabled ? 'Mute' : 'Unmute'}
          >
            {micEnabled ? <IconMicrophone size={21} /> : <IconMicOff size={21} />}
          </button>
          <button
            type="button"
            className="bz-acall-btn"
            onClick={toggleDevices}
            aria-label="Audio output"
            title="Audio output"
          >
            <IconSpeaker size={21} />
          </button>
          <button
            type="button"
            className="bz-acall-btn"
            onClick={openAdd}
            aria-label="Add someone"
            title="Add someone"
          >
            <IconPersonAdd size={21} />
          </button>
          <button
            type="button"
            className="bz-acall-hangup"
            onClick={onHangUp}
            aria-label="End call"
            title="End call"
          >
            <IconLeave size={22} />
          </button>
        </div>
        {devicesOpen && <DeviceMenu kinds={OUTPUT_KINDS} onClose={closeDevices} />}
      </div>

      {addOpen && (
        <NewCallDrawer
          title="Add to call"
          cta="Add to call"
          excludeIds={here}
          onClose={closeAdd}
          onStart={invite}
          onCopyLink={copyLink}
        />
      )}

      <RemoteAudio />
      {isHost && <KnockToast slug={slug} />}
    </div>
  );
}
